import { JSX, createRef, RefObject } from "react";
import { TreeDataGrid as MonzaTreeDataGrid } from "@monza/toolkit/tables/tree-data-grid/TreeDataGrid";
import { ITreeDataGridProps as IMonzaTreeDataGridProps, ITreeDataGridColumn as IMonzaTreeDataGridColumn, TreeDataGridRef } from "@monza/toolkit/tables/tree-data-grid/types";
import { ITableBaseColumn } from "@monza/toolkit/tables/types";
import { ColumnWidthUnits } from "@monza/toolkit/tables/enums";
import ThemeProvider from "@monza/toolkit/theme/ThemeProvider";
import DEFAULT_MONZO_THEME from "@monza/toolkit/theme/DEFAULT_THEME";
import { BaseComponent } from "../../Core/BaseComponent/BaseComponent";
import { CheckBox } from "../../Inputs/CheckBox/CheckBox";
import { Typography } from "../../Typography/Typography/Typography";
import { INavigationNode } from "@monza/toolkit/trees/treeview/types";
import { ExpandState } from "@monza/toolkit/trees/treeview/enums";
import "./Styles.scss";
import { ExpandedNodesHelper } from "./ExpandedNodesHelper";

export class TreeDataGrid<TNode extends object> extends BaseComponent<ITreeDataGridProps<TNode>, ITreeDataGridState>
{
    public static noDataLabel = '';

    private monzaRef: RefObject<TreeDataGridRef<TNode> | null> = createRef();
    private wrapperRef: RefObject<HTMLDivElement | null> = createRef();
    private syntheticRootId = crypto.randomUUID();
    private syntheticRoot = {} as TNode;

    constructor(props: ITreeDataGridProps<TNode>)
    {
        super(props);
        this.state = {};
    }

    public componentDidMount(): void
    {
        this.restoreExpansion();
    }

    public componentDidUpdate(prevProps: ITreeDataGridProps<TNode>): void
    {
        this.restoreExpansion();
        if (prevProps.selectedIds !== this.props.selectedIds)
        {
            this.monzaRef.current?.refreshAllRows();
        }
    }

    private restoreExpansion(): void
    {
        // setTimeout(0) defers until the toolkit has finished rebuilding the tree
        setTimeout(() =>
        {
            // only restore nodes whose entire ancestor chain is itself expanded because expandNode() force-expands a node's entire ancestor chain
            const { data, getNodeId, getParentNodeId, expandedIds } = this.props;
            const nodeHelper = new ExpandedNodesHelper(data, i => getNodeId(i), i => getParentNodeId(i), expandedIds ?? []);
            const nodeIdsToExpand = (expandedIds ?? []).filter(id => nodeHelper.areAllAncestorsExpanded(id));

            for (const id of nodeIdsToExpand)
            {
                this.monzaRef.current?.expandNode(id);
            }
        }, 0);
    }

    private handleSelectionChange(rowId: string, isChecked: boolean): void
    {
        const selectionMode = this.props.selectionMode ?? 'multi';
        const currentlySelectedIds = this.props.selectedIds ?? [];
        let newSelectedIds: string[];

        if (isChecked)
        {
            newSelectedIds = (selectionMode === 'single' ? [rowId] : Array.from(new Set([...currentlySelectedIds, rowId])));
        }
        else
        {
            newSelectedIds = currentlySelectedIds.filter(i => i !== rowId);
        }

        this.props.onSelectionChange?.(newSelectedIds);
    }

    private handleNodeStateChanged(node: TNode, column: IMonzaColumn<TNode>, currentView: string[]): void
    {
        if ('expandState' in node && 'id' in node)
        {
            const navigationNode = node as unknown as INavigationNode<TNode>;
            const currentExpanded = this.props.expandedIds ?? [];

            switch (navigationNode.expandState)
            {
                case ExpandState.EXPANDED:
                    if (!currentExpanded.includes(navigationNode.id))
                    {
                        this.props.onExpandedChange?.([...currentExpanded, navigationNode.id]);
                    }
                    break;

                case ExpandState.COLLAPSED:
                    if (currentExpanded.includes(navigationNode.id))
                    {
                        this.props.onExpandedChange?.(currentExpanded.filter(i => i !== navigationNode.id));
                    }
                    break;
            }
        }

        if ('onNodeStateChanged' in column)
        {
            column.onNodeStateChanged?.(node, currentView);
        }
    }

    private cachedColumns: Array<IMonzaColumn<TNode>> | undefined;
    private get columns(): Array<IMonzaColumn<TNode>>
    {
        const { columns, showCheckBoxes, getNodeId, getParentNodeId } = this.props;
        if (!this.cachedColumns)
        {
            const checkboxColumn: ITableBaseColumn<TNode> =
            {
                title: '',
                getCellValue: i => getNodeId(i),
                template: i => <div onClick={e => e.stopPropagation()}>
                    <CheckBox
                        label=""
                        checked={this.props.selectedIds?.includes(i.value as string)}
                        onCheckedChange={c => this.handleSelectionChange(i.value as string, c.checked === true)}
                    />
                </div>,
                columnWidth: 60,
                columnWidthUnits: ColumnWidthUnits.Pixels,
            };

            const monzaColumns = columns.map((column, index) =>
            {
                if (index === 0)
                {
                    return {
                        ...column,
                        getNodeId: (i: TNode) => (i === this.syntheticRoot ? this.syntheticRootId : getNodeId(i)),
                        getParentNodeId: (i: TNode) => (i === this.syntheticRoot ? null : (getParentNodeId(i) || this.syntheticRootId)),
                        onNodeStateChanged: (node: TNode, currentView: string[]) => this.handleNodeStateChanged(node, column, currentView),
                    } as IMonzaColumn<TNode>;
                }
                else
                {
                    return column;
                }
            });

            this.cachedColumns = (showCheckBoxes === true ? [checkboxColumn, ...monzaColumns] : monzaColumns);
        }
        return this.cachedColumns;
    }

    private cachedData: TNode[] | undefined;
    private cachedPropsData: TNode[] | undefined;
    private get data(): TNode[]
    {
        if (!this.cachedData || this.cachedPropsData !== this.props.data)
        {
            const data = [this.syntheticRoot, ...this.props.data];
            this.cachedPropsData = this.props.data;
            this.cachedData = data;
        }
        return this.cachedData;
    }

    public render(): JSX.Element
    {
        const {
            data, getNodeId, getParentNodeId, columns,
            showCheckBoxes, selectionMode, selectedIds, onSelectionChange,
            expandedIds, onExpandedChange,
            ...baseProps
        } = this.props;

        if (data.length === 0)
        {
            return (
                <div
                    className="tree-data-grid"
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '2rem',
                    }}
                >
                    <Typography
                        role='body'
                        size='medium'
                    >
                        {TreeDataGrid.noDataLabel}
                    </Typography>
                </div>
            );
        }

        return (
            <div
                className="tree-data-grid"
                ref={this.wrapperRef}
            >
                <ThemeProvider
                    theme={DEFAULT_MONZO_THEME}
                >
                    <MonzaTreeDataGrid<TNode>
                        {...baseProps}
                        data={this.data}
                        showRoot={false}
                        ref={this.monzaRef}
                        columns={this.columns}
                    />
                </ThemeProvider>
            </div>
        );
    }
}

export interface ITreeDataGridProps<TNode extends object> extends Omit<IMonzaTreeDataGridProps<TNode>, 'showRoot' | 'columns'>
{
    columns: Array<ITreeDataGridColumn<TNode>>;
    getNodeId: (item: TNode) => string;
    getParentNodeId: (item: TNode) => string | null;
    showCheckBoxes?: boolean;
    selectionMode?: SelectionMode;
    selectedIds?: string[];
    onSelectionChange?: (ids: string[]) => void;
    expandedIds?: string[];
    onExpandedChange?: (ids: string[]) => void;
}

type IMonzaColumn<TNode extends object> = ITableBaseColumn<TNode> | IMonzaTreeDataGridColumn<TNode>;
export type ITreeDataGridColumn<TNode extends object> = ITableBaseColumn<TNode> | Omit<IMonzaTreeDataGridColumn<TNode>, 'getNodeId' | 'getParentNodeId'>;
export interface ITreeDataGridState { }
export type SelectionMode = 'single' | 'multi';
export * from "@monza/toolkit/tables/tree-data-grid/types";
export * from "@monza/toolkit/tables/types";
export * from "@monza/toolkit/tables/enums";
